import type { ControlListItem } from "@/models/control.model";
import { extractMinMaxFromArray } from "@/shared/utils/data-table";
import type { Currency } from "@/shared/value-objects/currency.vo";
import { Money } from "@/shared/value-objects/money.vo";
import { NumberFilter } from "@spz/ui/components/number-filter";
import type { Table } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const isNumber = (value: number | undefined): value is number => {
  return typeof value === "number";
};

export const ControlListStakePopover = ({
  table,
  currency,
}: { table: Table<ControlListItem>; currency: Currency | undefined }) => {
  const { t } = useTranslation();

  const rawValue = table.getColumn("amount")?.getFilterValue();
  const minMaxValues = Array.isArray(rawValue) ? rawValue.join(",") : rawValue;
  const { min, max } = useMemo(() => {
    if (!currency) {
      return { min: undefined, max: undefined };
    }

    const { min: _min, max: _max } = extractMinMaxFromArray(minMaxValues);
    const min = isNumber(_min) ? Money.create(_min, currency) : undefined;
    const max = isNumber(_max) ? Money.create(_max, currency) : undefined;

    return {
      min: min?.amountUnit,
      max: max?.amountUnit,
    };
  }, [minMaxValues, currency]);

  const onFilterChange = (_min?: number, _max?: number) => {
    if (!currency) {
      return { min: undefined, max: undefined };
    }

    const min = isNumber(_min) ? Money.createByUnit(_min, currency) : undefined;
    const max = isNumber(_max) ? Money.createByUnit(_max, currency) : undefined;

    table.setColumnFilters((current) => {
      return [
        ...current,
        {
          id: "amount",
          value: [min?.amount, max?.amount],
        },
      ];
    });
  };

  const onReset = () => {
    table.setColumnFilters((current) =>
      current.filter((filter) => filter.id !== "amount"),
    );
  };

  return (
    <NumberFilter
      label={t("stake")}
      onApply={onFilterChange}
      onReset={onReset}
      min={min}
      max={max}
      data-testid="filter-stake-button"
    />
  );
};
